import { fetchFacesAlerts } from '@/libraries/api/facesAlerts'
import { imageForDetectionID } from '@/libraries/api/faces'
import moment from 'moment'
import { Service, Operation } from '@/libraries/permissions'

// Unacknowledged alerts stay on the alerts page until acknowledged, with no time cutoff.
// Acknowledged alerts stay visible this long after they happened.
export const ACKNOWLEDGED_VISIBLE_HOURS = 24

/**
 * "Acknowledged 9:14 am" (with the date when it wasn't today), plus "by <name>" when a PIN was
 * entered. Only the PIN's owner is named: the logged-in account is often a shared station.
 * @param {string} dismissedAt the alert's dismissed_at (UTC)
 * @param {object} [pinUserInfo] the PIN owner's users.end_user info, if a PIN was used
 */
export function acknowledgedText (dismissedAt, pinUserInfo) {
  const at = moment.utc(dismissedAt).local()
  const when = at.isSame(moment(), 'day') ? at.format('h:mm a') : at.format('MMM D, h:mm a')
  const name = pinUserInfo && pinUserInfo.name
  return name ? `Acknowledged ${when} by ${name}` : `Acknowledged ${when}`
}

/**
 * One entry per resident: their most recent alert, with their earlier alerts in `earlierAlerts`
 * (newest first). A resident is open (`dismissedAt` null) while any of their alerts is
 * unacknowledged, and `alertId` is then one of their open alerts, which is what Acknowledge sends.
 * Open residents come first, each list newest first.
 */
export function groupAlertsByResident (alerts) {
  const byResident = {}
  alerts.forEach(alert => {
    const key = alert.frontendFaceUUID || `alert-${alert.alertId}`
    byResident[key] = (byResident[key] || []).concat([alert])
  })
  const residents = Object.values(byResident).map(residentAlerts => {
    const sorted = residentAlerts.slice().sort((a, b) => b.unixTimestamp - a.unixTimestamp)
    const [latest, ...earlierAlerts] = sorted
    const openAlerts = sorted.filter(alert => !alert.dismissedAt)
    return {
      ...latest,
      alertId: openAlerts.length ? openAlerts[0].alertId : latest.alertId,
      dismissedAt: openAlerts.length ? null : latest.dismissedAt,
      openAlertCount: openAlerts.length,
      earlierAlerts,
    }
  })
  const newestFirst = (a, b) => b.unixTimestamp - a.unixTimestamp
  return [
    ...residents.filter(resident => !resident.dismissedAt).sort(newestFirst),
    ...residents.filter(resident => resident.dismissedAt).sort(newestFirst),
  ]
}

export const ERModule = {
  namespaced: true,
  state: {
    alerts: [],
    lastRefresh: moment().format('h:mm:ss a'),
  },
  mutations: {
    clearAlerts (state) {
      state.lastRefresh = moment().format('h:mm:ss a')
      state.alerts = []
    },
    /**
     * Fetches the newest alerts.
     *
     * @param {object} state The state of the store.
     * @param {array} data A list of alert objects.
     */
    updateAlerts (state, data) {
      state.alerts = data
      state.lastRefresh = moment().format('h:mm:ss a')
    },

  },
  getters: {
    alerts (state) {
      return state.alerts
    },
    lastRefresh (state) {
      return state.lastRefresh
    },
  },
  actions: {
    /**
     * Loads every unacknowledged alert (no time limit) plus the alerts acknowledged in the last
     * ACKNOWLEDGED_VISIBLE_HOURS, grouped per resident.
     *
     * Like the original, the returned promise never settles: refreshDataModel (store/actions.js)
     * waits on it in a Promise.all, so App.vue's redirect after refreshDataModel has never run.
     * Changing that is out of scope here.
     */
    checkMostRecent ({ dispatch }) {
      return new Promise(() => {
        dispatch('loadAlerts')
      })
    },
    loadAlerts ({ commit, rootGetters, dispatch }) {
      const canPerformOperation =
      rootGetters['entitlements/canPerformOperationForService']
      const canAccessElopement = canPerformOperation(
        Service.ALERTING,
        Operation.RECEIVE_ER_ALERTS,
      )
      if (!canAccessElopement) {
        return Promise.resolve()
      }
      const domainUUID = rootGetters.currentDomainUUID
      const acknowledgedSince = moment().subtract(ACKNOWLEDGED_VISIBLE_HOURS, 'hours')
      const load = params => fetchFacesAlerts({ domainUUID, sort: 'ascending', ...params })
        .then(res => res.data)
        .then(data => {
          if (!data.ok || !data.alerts) {
            throw new Error('No faces alerts.')
          }
          return data.alerts
        })

      return Promise.all([
        load({ includeDismissed: false }),
        load({ includeDismissed: true, since: acknowledgedSince.format() }),
      ])
        .then(([open, recent]) => {
          const seen = new Set(open.map(alert => alert.alert_id))
          return open.concat(recent.filter(alert => alert.dismissed_at && !seen.has(alert.alert_id)))
        })
        .then(alerts => Promise.all(alerts.map(alert => dispatch('transformAlert', alert))))
        .then(alerts => commit('updateAlerts', groupAlertsByResident(alerts)))
        .catch(error => console.log('Error: ', error))
    },
    transformAlert ({ commit, state }, alert) {
      return new Promise((resolve, reject) => {
        // Pull out some things we need.
        const timestamp = moment.utc(alert.event_ts)
        const isElopementAlert = alert.category === 'elopement_alert'
        const detectionId = alert.detection_id

        if (isElopementAlert) {
          const detection = {
            category: alert.category,
            alertId: alert.alert_id,
            unixTimestamp: timestamp.valueOf(),
            posterFrame: imageForDetectionID(detectionId),
            fullFrame: imageForDetectionID(detectionId, { fullFrame: true }),
            cameraDescription: alert.description,
            cameraUUID: alert.camera_uuid,
            frontendFaceUUID: alert.frontend_face_uuid,
            detectionId: detectionId,
            lastSeenTS: timestamp,
            dismissedAt: alert.dismissed_at,
            dismissedBy: alert.dismissed_by_end_user_info,
            dismissedByPin: alert.dismissed_by_pin_end_user_info,
          }
          resolve(detection)
        } else {
          reject(new Error(`Improperly formatted detection...`))
        }
      })
    },
    /** A live (socket) alert: reload, so it is grouped with that resident's other alerts. */
    incomingElopementAlert ({ dispatch }) {
      return dispatch('loadAlerts')
    },
    clearAlertsData ({ state, commit }) {
      commit('clearAlerts')
    },
  },
}
